import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';

type DataPoint = { x: number; y: number };

type GraphSwitchProps = {
	isVisible: boolean;
	label: string;
	icon: React.ReactElement;
} & React.ButtonHTMLAttributes<HTMLButtonElement>;

const ButtonSwitch = ({ isVisible, label, icon, ...props }: GraphSwitchProps) => {
	return (
		<button
			{...props}
			style={{
				display: 'flex',
				gap: '8px',
				alignItems: 'center',
				borderColor: isVisible ? '#08A2AA' : '#ABB7C3',
				borderStyle: 'solid',
				borderWidth: '3px',
				borderRadius: '10px',
				padding: '8px 10px',
				backgroundColor: 'white',
			}}
		>
			<svg width="20" height="20">
				{icon}
			</svg>
			<span style={{ color: isVisible ? 'black' : '#ABB7C3', fontSize: '18px' }}>{label}</span>
		</button>
	);
};

type LineChartProps = {
	data: {
		amount: DataPoint[];
		finalAmount: DataPoint[];
	};
};

const LineChart = ({ data }: LineChartProps) => {
	const [line1Visible, setLine1Visible] = useState(true);
	const [line2Visible, setLine2Visible] = useState(true);

	const svgRef = useRef<SVGSVGElement>(null);

	useEffect(() => {
		const svg = d3.select(svgRef.current);
		svg.selectAll('*').remove();

		const width = 900;
		const height = 300;
		const margin = 50;

		const allXValues = [...new Set([...data.amount.map((d) => d.x), ...data.finalAmount.map((d) => d.x)])].sort((a, b) => a - b);

		const allYValues = [...data.amount.map((d) => d.y), ...data.finalAmount.map((d) => d.y)];

		const yMin = Math.min(...allYValues);
		const yMax = Math.max(...allYValues);
		const xMax = Math.max(data.amount.length, data.finalAmount.length);

		const xScale = d3
			.scalePoint<number>()
			.domain(allXValues)
			.range([margin + 20, width - margin]);

		const yScale = d3
			.scaleLinear()
			.domain([yMin, yMax])
			.range([height - margin, margin]);

		const lineGenerator = d3
			.line<DataPoint>()
			.x((d) => xScale(d.x))
			.y((d) => yScale(d.y));

		svg
			.append('path')
			.attr('class', 'line1')
			.datum(data.amount)
			.attr('fill', 'none')
			.attr('stroke', '#C2C923')
			.attr('stroke-width', 2)
			.attr('d', lineGenerator);

		svg
			.append('path')
			.attr('class', 'line2')
			.datum(data.finalAmount)
			.attr('fill', 'none')
			.attr('stroke', '#08A2AA')
			.attr('stroke-width', 2)
			.attr('d', lineGenerator);

		svg
			.selectAll('.dot1')
			.data(data.amount)
			.enter()
			.append('circle')
			.attr('class', 'dot1')
			.attr('cx', (d) => xScale(d.x))
			.attr('cy', (d) => yScale(d.y))
			.attr('r', 5)
			.attr('fill', '#C2C923');

		const triangle = d3.symbol().type(d3.symbolTriangle).size(80);

		svg
			.selectAll('.dot2')
			.data(data.finalAmount)
			.enter()
			.append('path')
			.attr('class', 'dot2')
			.attr('d', triangle)
			.attr('fill', '#08A2AA')
			.attr('transform', (d) => `translate(${xScale(d.x)},${yScale(d.y)})`);

		const xAxis = d3.axisBottom(xScale).ticks(xMax);
		const yAxis = d3.axisLeft(yScale);

		svg
			.append('g')
			.attr('transform', `translate(0,${height - margin})`)
			.call(xAxis);

		svg.append('g').attr('transform', `translate(${margin},0)`).call(yAxis);
	}, [data.amount, data.finalAmount]);

	return (
		<div style={{ position: 'relative' }}>
			<svg ref={svgRef} width={900} height={300} />
			<div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', position: 'absolute', top: 0, right: 0 }}>
				<ButtonSwitch
					isVisible={line1Visible}
					label="Spesa prevista"
					icon={<circle cx="10" cy="10" r="7" fill="#C2C923" />}
					onClick={() => {
						const display = line1Visible ? 'none' : null;
						d3.selectAll('.line1').style('display', display);
						d3.selectAll('.dot1').style('display', display);
						setLine1Visible((isVisible) => !isVisible);
					}}
				/>
				<ButtonSwitch
					isVisible={line2Visible}
					label="Spesa effettuata"
					icon={<path d="M10 5 L15 15 L5 15 Z" fill="#08A2AA" />}
					onClick={() => {
						const display = line2Visible ? 'none' : null;
						d3.selectAll('.line2').style('display', display);
						d3.selectAll('.dot2').style('display', display);
						setLine2Visible((isVisible) => !isVisible);
					}}
				/>
			</div>
		</div>
	);
};

export default LineChart;
